import type { EntityState } from '@guest/shared';

export const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

export const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

export const isOn = (s: EntityState | undefined): boolean =>
  s?.state === 'on' || s?.state === 'open' || s?.state === 'playing' || s?.state === 'heat' || s?.state === 'cool';

export const isUnavailable = (s: EntityState | undefined): boolean =>
  s === undefined || s.state === 'unavailable' || s.state === 'unknown';

/** Lights report 0-255; the UI works in percent because guests think in percent. */
export const brightnessPct = (s: EntityState | undefined): number => {
  const b = num(s?.attributes['brightness']);
  return b === undefined ? 0 : Math.round((b / 255) * 100);
};

export const pctToBrightness = (pct: number): number =>
  Math.max(0, Math.min(255, Math.round((pct / 100) * 255)));

export const supportsBrightness = (s: EntityState | undefined): boolean => {
  const modes = strList(s?.attributes['supported_color_modes']);
  return modes.some((m) => m !== 'onoff' && m !== 'unknown');
};

export const supportsColorTemp = (s: EntityState | undefined): boolean =>
  strList(s?.attributes['supported_color_modes']).includes('color_temp');

/** Bit flags from HA's ClimateEntityFeature. */
const CLIMATE_TARGET_TEMPERATURE = 1;
const CLIMATE_PRESET_MODE = 16;

export interface ClimateInfo {
  current?: number;
  target?: number;
  min: number;
  max: number;
  step: number;
  modes: string[];
  fanModes: string[];
  presets: string[];
  preset?: string;
  action?: string;
  /** Heatmiser zones expose only `heat`, so there is no meaningful mode picker. */
  singleMode: boolean;
  supportsTarget: boolean;
  supportsPreset: boolean;
  /**
   * A Heatmiser stat sitting in standby reports `temperature: null` even though
   * it accepts a setpoint. Seed the stepper from its hold temperature so the
   * control is usable instead of silently absent.
   */
  fallbackTarget: number;
}

export function climateInfo(s: EntityState | undefined): ClimateInfo {
  const a = s?.attributes ?? {};
  const modes = strList(a['hvac_modes']);
  const features = num(a['supported_features']) ?? 0;
  const min = num(a['min_temp']) ?? 5;
  const max = num(a['max_temp']) ?? 35;
  const fallback = num(a['hold_temp']) ?? num(a['current_temperature']) ?? 18;
  return {
    ...(num(a['current_temperature']) !== undefined ? { current: num(a['current_temperature'])! } : {}),
    ...(num(a['temperature']) !== undefined ? { target: num(a['temperature'])! } : {}),
    min,
    max,
    step: num(a['target_temp_step']) ?? 0.5,
    modes,
    fanModes: strList(a['fan_modes']),
    presets: strList(a['preset_modes']),
    ...(str(a['preset_mode']) ? { preset: str(a['preset_mode'])! } : {}),
    ...(str(a['hvac_action']) ? { action: str(a['hvac_action'])! } : {}),
    singleMode: modes.length <= 1,
    supportsTarget: (features & CLIMATE_TARGET_TEMPERATURE) !== 0,
    supportsPreset: (features & CLIMATE_PRESET_MODE) !== 0,
    fallbackTarget: Math.min(max, Math.max(min, Math.round(fallback * 2) / 2)),
  };
}

export const HVAC_LABELS: Record<string, string> = {
  off: 'Off',
  heat: 'Heat',
  cool: 'Cool',
  heat_cool: 'Auto',
  auto: 'Auto',
  dry: 'Dry',
  fan_only: 'Fan',
};

export const PRESET_LABELS: Record<string, string> = {
  home: 'Home',
  boost: 'Boost',
  away: 'Away',
  standby: 'Standby',
  eco: 'Eco',
  comfort: 'Comfort',
  sleep: 'Sleep',
  none: 'None',
};

/** Sonos reports its own entity id in group_members even when ungrouped. */
export function groupMembers(s: EntityState | undefined, self: string): string[] {
  return strList(s?.attributes['group_members']).filter((m) => m !== self);
}

export const ALARM_LABELS: Record<string, string> = {
  disarmed: 'Disarmed',
  armed_home: 'Armed — home',
  armed_away: 'Armed — away',
  armed_night: 'Armed — night',
  armed_vacation: 'Armed — vacation',
  arming: 'Arming…',
  pending: 'Entry delay',
  triggered: 'Triggered',
  disarming: 'Disarming…',
  unavailable: 'Unavailable',
  unknown: 'Unknown',
};

export function formatSensor(s: EntityState | undefined): string {
  if (!s) return '—';
  if (s.state === 'unavailable' || s.state === 'unknown') return '—';
  const unit = str(s.attributes['unit_of_measurement']);
  const asNum = Number(s.state);
  if (Number.isFinite(asNum)) {
    const decimals = Math.abs(asNum) >= 100 ? 0 : 1;
    return `${asNum.toFixed(decimals)}${unit ? `${unit === '%' ? '' : ' '}${unit}` : ''}`;
  }
  return binaryLabel(s);
}

export function binaryLabel(s: EntityState): string {
  const dc = str(s.attributes['device_class']);
  const on = s.state === 'on';
  switch (dc) {
    case 'motion':
    case 'occupancy':
      return on ? 'Movement' : 'Still';
    case 'door':
    case 'window':
    case 'opening':
    case 'garage_door':
      return on ? 'Open' : 'Closed';
    case 'moisture':
      return on ? 'Wet' : 'Dry';
    default:
      return on ? 'Yes' : 'No';
  }
}

export function mediaSummary(s: EntityState | undefined): string {
  if (!s || s.state === 'unavailable') return 'Unavailable';
  const title = str(s.attributes['media_title']);
  const artist = str(s.attributes['media_artist']);
  if (s.state === 'playing' || s.state === 'paused') {
    const base = title ? (artist ? `${title} — ${artist}` : title) : 'Playing';
    return s.state === 'paused' ? `Paused · ${base}` : base;
  }
  if (s.state === 'idle' || s.state === 'off') return 'Nothing playing';
  return s.state;
}
